import React from 'react';
import { includes, filter, isEmpty, get, map, every, cloneDeep, set } from 'lodash';

// local dependencies
import { callResolutionReporting as CallResolutionReportingView } from 'ui/views/widgets/callResolutionReporting';
import { CallResolutionReportingService } from 'services/callResolutionReporting';
import { SERVICE_RESPONSE_STATUS, SELECT_ALL_OPTION_VALUE } from 'constants/common';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import { reportCallCallerTypeselectionResolver } from 'resolvers/reportCallCallerTypeselection';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CallResolutionReporting } from 'ui/containers/CallResolutionReporting';
 *
 * // render
 * <CallResolutionReporting propName='propValue'/>
 */

// get store
const store = getStore();
export class CallResolutionReporting extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.state = {
            isFetching: true,
            componentData: undefined,
            selectedDuration: undefined,
            barGraphData: {},
            pieChartData: {},

            isBarChartFetching: true,
            isPieChartFetching: false,
            networkStatus: undefined,
            isPieChartVisible: false,
            barGraphNetworkStatus: undefined
        };

        this._bind();

        // create service instance
        this.callResolutionReportingService = new CallResolutionReportingService();
        
        // start component data fetch
        this.fetchComponentData();
    }

    _bind() {
        this.fetchDataForGraph = this.fetchDataForGraph.bind( this );
        this.handleBarChartClick = this.handleBarChartClick.bind( this );
        this.handleOptionSelect = this.handleOptionSelect.bind( this );
        this.handleCagChange = this.handleCagChange.bind( this );
        this.fetchDataForChart = this.fetchDataForChart.bind( this );
        this.getSelection = this.getSelection.bind( this );
    }

    // fetch Component Data
    async fetchComponentData() {
        log.debug( 'CallResolutionReporting.fetchComponentData()' );

        // cancel ongoing request
        this.callResolutionReportingService.cancel();

        // get status and service data
        const { status, data } = await this.callResolutionReportingService.fetchComponentData( {
            url: this.props.widget.url
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                isBarChartFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            const responseData = cloneDeep( data );
            const selectAll = [ { name: SELECT_ALL_OPTION_VALUE, title: { refId: '', value: 'All' }, value: SELECT_ALL_OPTION_VALUE } ];

            const callerMood = [ ...selectAll, ...get( responseData, 'options.callerMood' ) ];
            set( responseData, 'filterData.options.callerMood', callerMood );

             // fetch Caller Types from the store
             const _callerTypeOptions = cloneDeep( get( store.getState()[ REDUCER.APP_CONTEXT ], 'component.data.options.callerType', null ) );
             const callerTypeOptions = [ ...selectAll, ..._callerTypeOptions ];
             set( responseData, 'filterData.options.callerTypeOptions', callerTypeOptions );
 
             // fetch Call Types from the store
            const callCallerType = get( data, 'options.callCallerType' );
            const callCallerTypeOptions = [ ...selectAll, ...callCallerType ];

            set( responseData, 'filterData.options.callCallerType', callCallerTypeOptions );

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: responseData,
               
            }, () => this.fetchDataForGraph() );

        }
    }


    // fetch Data for the graphs
     async fetchDataForGraph() {
        log.debug( 'CallResolutionReporting.fetchDataForGraph()' );

        // cancel ongoing request
        this.callResolutionReportingService.cancel();

        // remove all other Options if All is selected
        const filters = cloneDeep( get( this.state, 'componentData.filterData.filter' ) );
        Object.keys( filters ).forEach( function( key ) {
            includes( filters[ key ], SELECT_ALL_OPTION_VALUE ) ? filters[ key ] = [ SELECT_ALL_OPTION_VALUE ] : null;
        } );

        // get status and service data
        const { status, data } = await this.callResolutionReportingService.fetchGraphData( {
            url: this.state.componentData.structure.filtersConfig.url,
            data: {
                context: {},
                data: {
                   filter: filters
                }
             }
        } );

        // pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isBarChartFetching: false,
                barGraphNetworkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            this.setState( {
                isBarChartFetching: false,
                barGraphNetworkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                barGraphData: data
            } );
        }
    }

    async fetchDataForChart( duration ) {
        log.debug( 'CallResolutionReporting.fetchDataForChart()' );

        pageLevelPreloader.show();

        // cancel ongoing request
        this.callResolutionReportingService.cancel();

        const { status, data } = await this.callResolutionReportingService.fetchPieChartData( {
            url: this.state.barGraphData.config.reportConfig.url,
            data: {
                context: {},
                data: {
                   filter: this.state.componentData.filterData.filter,
                   [ this.state.barGraphData.config.reportConfig.payloadKey ]: duration
                }
             }
        } );

        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

            errorOverlay.open();

            this.setState( {
                isPieChartFetching: false,
                pieChartData: {},
                isPieChartVisible: false,
            } );
        } else {
            this.setState( {
                isPieChartFetching: false,
                pieChartData: data,
            } );
        }
    }

    // handle click of option selection
    handleOptionSelect( { option, options, selected, optionType, isSelectAllVisible } ) {
        log.debug( 'CallResolutionReporting.handleOptionSelect()' );
        
        const selectedOptions = 'cag' !== optionType ? this.getSelection( option, options, selected, isSelectAllVisible ) : null;
        const data = { ...this.state.componentData };

        data.filterData.filter [ optionType ] = 'cag' !== optionType ? selectedOptions : options;

        if( 'callType' === optionType || 'callerType' === optionType ) {
            const _filterData = reportCallCallerTypeselectionResolver( { filterData: data.filterData, optionType, optionValue: get( option, 'value' ) } );
            set( data, 'filterData', _filterData );
        }

        this.setState( {
            componentData: data,
            isBarChartFetching: true,
            networkStatus: undefined,
            isPieChartVisible: false,
        }, () => {

                // pageLevelPreloader.show();
                this.fetchDataForGraph();
        } );
    }

    handleBarChartClick( { payload } ) {
        log.debug( 'CallResolutionReporting.handleBarChartClick()' );
        this.setState( {
            isPieChartFetching: true,
            networkStatus: undefined,
            isPieChartVisible: true,
        }, () => {
            this.fetchDataForChart( payload.id );
        } );
    }

    getSelection( option, options, selected, isSelectAllVisible ) {
        log.debug( 'CallResolutionReporting.getSelection()' );

        // new selected value
        let selection = [];
        const selectableOptions = filter( options, _option => ! isEmpty( _option[ 'value' ] ) ); // `labels` excluded
        const selectableOptionsExceptSelectAll = isSelectAllVisible ? filter( selectableOptions, _option => _option[ 'value' ] !== SELECT_ALL_OPTION_VALUE ) : selectableOptions; // remove `selectAll` option

        if( includes( selected, option[ 'value' ] ) ) {
            if( isSelectAllVisible && option[ 'value' ] === SELECT_ALL_OPTION_VALUE ) {
                selection = []; // wipe all selection when `selectAll` option is unselected
            } else {
                selection = filter( selected, _value => {
                    return _value !== option[ 'value' ] && _value !== SELECT_ALL_OPTION_VALUE; // remove `selectAll` option as well
                } );
            }
        } else {
            if( isSelectAllVisible && option[ 'value' ] === SELECT_ALL_OPTION_VALUE ) {
                selection = map( selectableOptions, 'value' ); // select all options when `selectAll` option is selected
            } else {
                selection = selected.concat( option[ 'value' ] );
            }
        }

        // if all options are selected, also select `selectAll` option
        if( isSelectAllVisible && ! includes( selection, SELECT_ALL_OPTION_VALUE ) ) {
            const areAllOptionsSelected = every( selectableOptionsExceptSelectAll, ( _option ) => includes( selection, _option[ 'value' ] ) );

            if( areAllOptionsSelected ) {
                selection = map( selectableOptions, 'value' );
            }
        }
        return selection;
    }

    handleCagChange( values = {} ) {
        log.debug( 'CallResolutionReporting.handleCagChange()', values );
        this.handleOptionSelect( { options: values, optionType: 'cag' } );
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <CallResolutionReportingView
                isFetching={ this.state.isFetching }
                isBarChartFetching={ this.state.isBarChartFetching }
                selectedDuration={ this.state.selectedDuration }
                barGraphData={ this.state.barGraphData }
                onBarChartClickHandler={ this.handleBarChartClick }
                { ...get( this.state, 'componentData' ) }

                isPieChartFetching={ this.state.isPieChartFetching }
                networkStatus={ this.state.networkStatus }
                onOptionSelect={ this.handleOptionSelect }
                pieChartData={ this.state.pieChartData }
                isPieChartVisible={ this.state.isPieChartVisible }
                barGraphNetworkStatus={ this.state.barGraphNetworkStatus }
            />
        );
    }
}

// set display name
CallResolutionReporting.displayName = 'CallResolutionReporting';

// set default props
CallResolutionReporting.defaultProps = {};

// prop types
CallResolutionReporting.propTypes = {};
